/**
 * CredentialFormModal - Dynamic form for API Key and Basic Auth connectors
 * v2.26 - 2026-01-30 - Initial implementation
 *
 * Renders a dynamic form based on the form_schema returned by the backend.
 * Supports:
 * - API Key entry (single field)
 * - Basic Auth (username + password)
 * - Additional fields as specified by connector config
 * - Delegation option ("Send to my accountant")
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Link,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

// Form field from backend schema
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'email';
  required: boolean;
  placeholder?: string;
  help_text?: string;
}

// Form schema from backend
export interface FormSchema {
  title: string;
  description: string;
  connector_type: string;
  fields: FormField[];
  submit_label: string;
  cancel_label: string;
}

interface CredentialFormModalProps {
  open: boolean;
  onClose: () => void;
  formSchema: FormSchema | null;
  connectorDisplayName: string;
  onSubmit: (data: Record<string, string>) => Promise<void>;
  onDelegateClick?: () => void;
  loading?: boolean;
  error?: string | null;
}

const CredentialFormModal: React.FC<CredentialFormModalProps> = ({
  open,
  onClose,
  formSchema,
  connectorDisplayName,
  onSubmit,
  onDelegateClick,
  loading = false,
  error = null,
}) => {
  // Form state - dynamic based on schema fields
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens with new schema
  React.useEffect(() => {
    if (open && formSchema) {
      const initialData: Record<string, string> = {};
      formSchema.fields.forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);
      setValidationErrors({});
    }
  }, [open, formSchema]);

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear validation error when user types
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!formSchema) return false;

    const errors: Record<string, string> = {};
    formSchema.fields.forEach(field => {
      if (field.required && !formData[field.name]?.trim()) {
        errors[field.name] = `${field.label} is required`;
      }
      // URL validation
      if (field.type === 'url' && formData[field.name]) {
        try {
          new URL(formData[field.name]);
        } catch {
          errors[field.name] = 'Please enter a valid URL';
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    await onSubmit(formData);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({});
      setValidationErrors({});
      onClose();
    }
  };

  if (!formSchema) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          {formSchema.title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {formSchema.description}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {formSchema.fields.map((field) => (
            <Box key={field.name}>
              <TextField
                fullWidth
                label={field.label}
                type={field.type === 'password' ? 'password' : 'text'}
                required={field.required}
                placeholder={field.placeholder}
                value={formData[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                error={!!validationErrors[field.name]}
                helperText={validationErrors[field.name] || field.help_text}
                disabled={loading}
                autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                InputProps={{
                  sx: { borderRadius: 1 }
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Delegation option */}
        {onDelegateClick && (
          <>
            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">
                or
              </Typography>
            </Divider>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                startIcon={<PersonAddIcon />}
                onClick={onDelegateClick}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                I don't have access - send to my accountant
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ color: 'text.secondary' }}
        >
          {formSchema.cancel_label}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            minWidth: 120,
          }}
        >
          {loading ? 'Connecting...' : formSchema.submit_label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CredentialFormModal;
